import { useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "../components/Icon";
import { LoadingState } from "../components/LoadingState";
import { supabase } from "../lib/supabase";
import type { AvailabilityRule, HostProfile } from "../lib/types";

const WEEKDAYS = [
  { value: 1, short: "Mon", label: "Monday" },
  { value: 2, short: "Tue", label: "Tuesday" },
  { value: 3, short: "Wed", label: "Wednesday" },
  { value: 4, short: "Thu", label: "Thursday" },
  { value: 5, short: "Fri", label: "Friday" },
  { value: 6, short: "Sat", label: "Saturday" },
  { value: 7, short: "Sun", label: "Sunday" },
] as const;

const DEMO_HOSTS: HostProfile[] = [
  { id: 1, organization_id: 1, user_id: null, slug: "shahil", display_name: "Shahil Mathapershad", domain_label: "Capital & Credit", role: "Quantitative Analyst, ABSA Group", bio: "", timezone: "Africa/Johannesburg", is_active: true },
  { id: 2, organization_id: 1, user_id: null, slug: "rashik", display_name: "Rashik Singh", domain_label: "Operations & Supply", role: "Industrial Pharmacist, Medipost Holdings", bio: "", timezone: "Africa/Johannesburg", is_active: true },
];

function demoRules(): AvailabilityRule[] {
  return DEMO_HOSTS.flatMap((host) => WEEKDAYS.map(({ value }) => ({
    organization_id: host.organization_id,
    host_profile_id: host.id,
    weekday: value,
    start_time: "09:00",
    end_time: host.slug === "rashik" ? "23:00" : "17:00",
    timezone: host.timezone,
    is_active: host.slug === "rashik" || value <= 5,
  })));
}

const keyFor = (hostId: number, weekday: number) => `${hostId}:${weekday}`;
const toInputTime = (value: string) => value.slice(0, 5);

export function AvailabilityPage() {
  const [hosts, setHosts] = useState<HostProfile[]>([]);
  const [rules, setRules] = useState<AvailabilityRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingHost, setSavingHost] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!supabase) {
      setHosts(DEMO_HOSTS);
      setRules(demoRules());
      setLoading(false);
      return;
    }
    setError(null);
    const [hostResult, ruleResult] = await Promise.all([
      supabase.from("host_profiles").select("id, organization_id, user_id, slug, display_name, domain_label, role, bio, timezone, is_active").eq("is_active", true).order("id"),
      supabase.from("availability_rules").select("id, organization_id, host_profile_id, weekday, start_time, end_time, timezone, is_active").order("weekday"),
    ]);
    if (hostResult.error || ruleResult.error) {
      setError(hostResult.error?.message ?? ruleResult.error?.message ?? "Availability could not be loaded.");
    } else {
      setHosts((hostResult.data ?? []) as HostProfile[]);
      setRules((ruleResult.data ?? []) as AvailabilityRule[]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  const ruleMap = useMemo(() => new Map(rules.map((rule) => [keyFor(rule.host_profile_id, rule.weekday), rule])), [rules]);

  const ruleFor = (host: HostProfile, weekday: number): AvailabilityRule => ruleMap.get(keyFor(host.id, weekday)) ?? {
    organization_id: host.organization_id,
    host_profile_id: host.id,
    weekday,
    start_time: "09:00",
    end_time: "17:00",
    timezone: host.timezone,
    is_active: false,
  };

  const updateRule = (host: HostProfile, weekday: number, patch: Partial<AvailabilityRule>) => {
    const next = { ...ruleFor(host, weekday), ...patch };
    setRules((current) => [...current.filter((rule) => keyFor(rule.host_profile_id, rule.weekday) !== keyFor(host.id, weekday)), next]);
    setMessage(null);
  };

  const save = async (host: HostProfile) => {
    const hostRules = WEEKDAYS.map(({ value }) => ruleFor(host, value));
    const invalid = hostRules.find((rule) => rule.is_active && rule.start_time >= rule.end_time);
    if (invalid) {
      setError("Each active day must end after it starts.");
      return;
    }
    setSavingHost(host.id);
    setError(null);
    setMessage(null);
    if (supabase) {
      const payload = hostRules.map((rule) => ({
        organization_id: rule.organization_id,
        host_profile_id: rule.host_profile_id,
        weekday: rule.weekday,
        start_time: toInputTime(rule.start_time),
        end_time: toInputTime(rule.end_time),
        timezone: rule.timezone,
        is_active: rule.is_active,
      }));
      const { error: saveError } = await supabase.from("availability_rules").upsert(payload, { onConflict: "host_profile_id,weekday" });
      if (saveError) setError(saveError.message);
      else {
        setMessage(`${host.display_name.split(" ")[0]}'s hours are live.`);
        await load();
      }
    } else {
      setMessage(`${host.display_name.split(" ")[0]}'s preview schedule is saved in this tab.`);
    }
    setSavingHost(null);
  };

  return (
    <main className="settings-page">
      <header className="settings-hero">
        <div><p className="section-label">Scheduling rules</p><h1>Availability</h1></div>
        <p>Set the recurring windows guests can book. Existing meetings and buffers are removed automatically.</p>
      </header>
      {error ? <div className="settings-message settings-message--error" role="alert">{error}</div> : null}
      {message ? <div className="settings-message" role="status"><Icon name="check" /> {message}</div> : null}
      {loading ? <LoadingState label="Loading availability" /> : (
        <div className="schedule-stack">
          {hosts.map((host) => (
            <section className="schedule-card" key={host.id} aria-labelledby={`host-${host.id}`}>
              <header>
                <div><p>{host.domain_label}</p><h2 id={`host-${host.id}`}>{host.display_name}</h2></div>
                <span>{host.timezone.replaceAll("_", " ")}</span>
              </header>
              <div className="schedule-days">
                {WEEKDAYS.map((day) => {
                  const rule = ruleFor(host, day.value);
                  return (
                    <div className={`schedule-day${rule.is_active ? " is-active" : ""}`} key={day.value}>
                      <label className="day-toggle">
                        <input type="checkbox" checked={rule.is_active} onChange={(event) => updateRule(host, day.value, { is_active: event.target.checked })} />
                        <span aria-hidden="true" />
                        <strong><abbr title={day.label}>{day.short}</abbr></strong>
                      </label>
                      <div className="day-window">
                        <label><span className="sr-only">{day.label} starts</span><input type="time" value={toInputTime(rule.start_time)} disabled={!rule.is_active} onChange={(event) => updateRule(host, day.value, { start_time: event.target.value })} /></label>
                        <i aria-hidden="true">—</i>
                        <label><span className="sr-only">{day.label} ends</span><input type="time" value={toInputTime(rule.end_time)} disabled={!rule.is_active} onChange={(event) => updateRule(host, day.value, { end_time: event.target.value })} /></label>
                      </div>
                      <small>{rule.is_active ? "Open" : "Unavailable"}</small>
                    </div>
                  );
                })}
              </div>
              <footer>
                <p>Times are published in {host.timezone.replaceAll("_", " ")}.</p>
                <button className="button button--primary" type="button" onClick={() => void save(host)} disabled={savingHost === host.id}>{savingHost === host.id ? "Saving…" : "Save schedule"}</button>
              </footer>
            </section>
          ))}
        </div>
      )}
    </main>
  );
}
